import React, { useEffect, useState } from 'react';
import { Grid, Tooltip, useMediaQuery } from '@mui/material';
import { ColorDot } from '../../atoms/ColorDot';
import { Typography, TypographyVariantProps } from '../../atoms/Typography';
import { EditField } from '../../molecules/EditField';
import { EditDropdownField } from '../../molecules/EditDropdownField';
import { EditDateTimePicker } from '../../molecules/EditDateTimePicker';
import { EditPrioritySelectMenu } from '../../organisms/EditPrioritySelectMenu';
import { ServiceSummaryProps } from './ServiceSummary.props';
import { GridAlignContainer, MobileItemGrid, TitleLabel, WebItemGrid } from './ServiceSummary.styles';
import { EditLiveSearchField } from '../../molecules/EditLiveSearchField/EditLiveSearchField';
import { LocationNodeData, ScreenSize } from '../../../@types';
import { COLORS } from '../../../configs/colors';
import { actionTypesData, processTypesData, serviceTypesData } from '../../../utils/services';
import { SERVICE_PRESENTATION_OPTIONS } from '../../../constants/interaction.constant';

export const ServiceSummary: React.FC<ServiceSummaryProps> = ({
  permissions,
  options,
  value,
  onChange
}) => {
  const [isMobile, setIsMobile] = useState(false);

  const isMobileView = useMediaQuery(ScreenSize.MOBILE);

  useEffect(() => {
    setIsMobile(isMobileView);
  }, [isMobileView]);

  return (
    <>
      {!isMobile ?
        <Grid container mt={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Client / Contract</TitleLabel>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Tooltip title={`${value?.client}${value?.contractName ? `/${value?.contractName}` : ''}`} placement="top-start">
                  <span>
                    <Typography variant={TypographyVariantProps.Body1}>{value.clientCode || ''} / {value.contractCode || ''}</Typography>
                  </span>
                </Tooltip>
              </Grid>
            </WebItemGrid> 
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Customer ref</TitleLabel>
              </Grid>
              <GridAlignContainer size={{ xs: 12, md: 6 }}>
                <EditField
                  isEditable={permissions.isReferenceNo1}
                  value={value.referenceNo1 || ''}
                  onChange={(val) => onChange({
                    ...value,
                    referenceNo1: val
                  })}
                />
              </GridAlignContainer>
            </WebItemGrid>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Logged at</TitleLabel>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <EditDateTimePicker
                  isEditable={permissions.isLoggedAt}
                  value={value.loggedAt || ''}
                  onChange={(val) => onChange({
                    ...value,
                    loggedAt: val
                  })} />
              </Grid>
            </WebItemGrid>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Logged by</TitleLabel>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <EditField
                  isEditable={permissions.isLoggedBy}
                  value={value.loggedBy || ''}
                  onChange={(val) => onChange({
                    ...value,
                    loggedBy: val
                  })} />
              </Grid>
            </WebItemGrid>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Sublocation</TitleLabel>
              </Grid>
              <GridAlignContainer size={{ xs: 12, md: 6 }}>
                <ColorDot color="gray" />
                <EditLiveSearchField
                  isEditable={permissions.isSubLocation}
                  mainLocationId={value.mainLocationId}
                  subLocationId={value.subLocationId}
                  value={value.subLocation || ''}
                  onChange={async (obj: LocationNodeData) => onChange({
                    ...value,
                    subLocation: obj.name,
                    subLocationId: obj.id
                  })} />
              </GridAlignContainer>
            </WebItemGrid>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Zone</TitleLabel>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <span>
                  <Typography variant={TypographyVariantProps.Body1}>{value.zone || ''}</Typography>
                </span>
              </Grid>
            </WebItemGrid>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Brand</TitleLabel>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <EditField
                  isEditable={permissions.isBrand}
                  value={value.brand || ''}
                  onChange={(val) => onChange({
                    ...value,
                    brand: val
                  })}
                />
              </Grid>
            </WebItemGrid>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Priority</TitleLabel>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <EditPrioritySelectMenu
                  id="service-summary-priority-select"
                  isEditable={permissions.isPriority}
                  items= {options.priorityAndServicesOptions?.map((obj: any) => ({ 
                    value: obj.id.toString(),
                    label: obj.value.shortName,
                    color: obj.value.color,
                    description: obj.value.description,
                    disabled: !obj.value.isActive
                  })) || []}
                  value={value.priorityId?.toString() || ''}
                  onChange={(val) => onChange({
                    ...value,
                    priorityId: val
                  })}
                  priorityLabel={value.priorityLabel}
                  priorityColor={value.priorityColor}
                />
              </Grid>
            </WebItemGrid>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Service Type</TitleLabel>
              </Grid>
              <GridAlignContainer size={{ xs: 12, md: 6 }}>
                <ColorDot color={COLORS.DarkCyan} />
                <EditDropdownField
                  textColor={COLORS.DarkCyan}
                  textFontSize=""
                  isEditable={permissions.isServiceType}
                  options={serviceTypesData(options.serviceOptions || [])}
                  value={value.serviceType || '-1'}
                  onChange={(val) => onChange({
                    ...value,
                    serviceType: val
                  })}
                  validate={true}
                  optionalLabel="Select Service Type"
                  optionalLabelEnabled={true}
                  errorMessageEnabled={false}
                />
              </GridAlignContainer>
            </WebItemGrid>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Action Type</TitleLabel>
              </Grid>
              <GridAlignContainer size={{ xs: 12, md: 6 }}>
                <ColorDot color={COLORS.LightCyan} />
                <EditDropdownField
                  textColor={COLORS.LightCyan}
                  textFontSize=""
                  isEditable={permissions.isActionType}
                  options={actionTypesData(options.actionTypeOptions || [])}
                  value={value.actionType || ''}
                  onChange={(val) => onChange({
                    ...value,
                    actionType: val
                  })}
                  validate={true}
                  optionalLabel="Select Action Type"
                  optionalLabelEnabled={true}
                  errorMessageEnabled={false}
                />
              </GridAlignContainer>
            </WebItemGrid>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Process Type</TitleLabel>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <EditDropdownField
                  textColor={COLORS.Red}
                  isEditable={permissions.isProcessType}
                  options={processTypesData(options.processTypeOptions || [])}
                  value={value.processType || ''}
                  onChange={(val) => onChange({
                    ...value,
                    processType: val
                  })}
                  validate={true}
                  optionalLabel="Select Process Type"
                  optionalLabelEnabled={true}
                  errorMessageEnabled={false}
                />
              </Grid>
            </WebItemGrid>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Site Schema</TitleLabel>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {value.siteSchema || '-'}
              </Grid>
            </WebItemGrid>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {permissions.isClientSLA &&
          <WebItemGrid container>
            <Grid size={{ xs: 12, md: 6 }}>
              <TitleLabel>Client SLA</TitleLabel>
            </Grid>
            <GridAlignContainer size={{ xs: 12, md: 6 }}>
              <ColorDot color={value.isClientSLABreached ? COLORS.Red : COLORS.Green} />
              <Typography textColor={value.isClientSLABreached ? COLORS.Red : COLORS.Green}>{value.clientSLA}</Typography>
            </GridAlignContainer>
          </WebItemGrid>
            }
            {permissions.isOpsSLA && 
          <WebItemGrid container>
            <Grid size={{ xs: 12, md: 6 }}>
              <TitleLabel>Ops SLA</TitleLabel>
            </Grid>
            <GridAlignContainer size={{ xs: 12, md: 6 }}>
              <ColorDot color={value.isOpsSLABreached ? COLORS.Red : COLORS.Green} />
              <Typography textColor={value.isOpsSLABreached ? COLORS.Red : COLORS.Green}>{value.opsSLA}</Typography>
            </GridAlignContainer>
          </WebItemGrid>
            }
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Scheduled</TitleLabel>
              </Grid>
              <GridAlignContainer size={{ xs: 12, md: 6 }}>
                <ColorDot color="gray" />
                <EditDateTimePicker
                  isEditable={permissions.isScheduled}
                  value={value.scheduled || ''}
                  disablePast={true}
                  onChange={(val) => onChange({
                    ...value,
                    scheduled: val
                  })}
                />
              </GridAlignContainer>
            </WebItemGrid>
            <WebItemGrid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <TitleLabel>Follow Up</TitleLabel>
              </Grid>
              <GridAlignContainer size={{ xs: 12, md: 6 }}>
                <ColorDot color="gray" />
                <EditDateTimePicker
                  isEditable={permissions.isFollowUp}
                  value={value.followUp || ''}
                  disablePast={true}
                  onChange={(val) => onChange({
                    ...value,
                    followUp: val
                  })}
                />
              </GridAlignContainer>
            </WebItemGrid>
            {value.presentation &&
              <WebItemGrid container>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TitleLabel>Presentation</TitleLabel>
                </Grid>
                <GridAlignContainer size={{ xs: 12, md: 'grow' }}>
                  <ColorDot color={COLORS.Gray} />
                  <EditDropdownField
                    textColor={COLORS.Black}
                    textFontSize=""
                    isEditable={permissions.isPresentation}
                    options={SERVICE_PRESENTATION_OPTIONS}
                    value={value.presentation || '-1'}
                    onChange={(val) => onChange({
                      ...value,
                      presentation: val
                    })}
                    validate={true}
                    optionalLabel="Select Presentation"
                    optionalLabelEnabled={true}
                    errorMessageEnabled={false}
                  />
                </GridAlignContainer>
              </WebItemGrid>
            }
          </Grid>
        </Grid>
        :
        <Grid container mt={2} flexDirection="column">
          <MobileItemGrid container>
            <Grid size={{ xs: 6, md: 6 }}>
              <TitleLabel>Client / Contract</TitleLabel>
            </Grid>
            <Grid size={{ xs: 6, md: 6 }}>
              <Tooltip title={`${value?.client}${value?.contractName ? `/${value?.contractName}` : ''}`} placement="top-start">
                <span>
                  <Typography variant={TypographyVariantProps.Body1}>{value.clientCode || ''} / {value.contractCode || ''}</Typography>
                </span>
              </Tooltip>
            </Grid>
          </MobileItemGrid>
          <MobileItemGrid container>
            <Grid size={{ xs: 6, md: 6 }}>
              <TitleLabel>Brand</TitleLabel>
            </Grid>
            <Grid size={{ xs: 6, md: 6 }}>
              <EditField
                isEditable={permissions.isBrand}
                value={value.brand || ''}
                onChange={(val) => onChange({
                  ...value,
                  brand: val
                })}
              />
            </Grid>
          </MobileItemGrid>
          {permissions.isClientSLA &&
              <MobileItemGrid container>
                <Grid size={{ xs: 6, md: 6 }}>
                  <TitleLabel>Client SLA</TitleLabel>
                </Grid>
                <GridAlignContainer size={{ xs: 6, md: 6 }}>
                  <ColorDot color={value.isClientSLABreached ? COLORS.Red : COLORS.Green} />
                  <Typography textColor={value.isClientSLABreached ? COLORS.Red : COLORS.Green}>{value.clientSLA}</Typography>
                </GridAlignContainer>
              </MobileItemGrid>
          }
          {permissions.isOpsSLA &&
              <MobileItemGrid container>
                <Grid size={{ xs: 6, md: 6 }}>
                  <TitleLabel>Ops SLA</TitleLabel>
                </Grid>
                <GridAlignContainer size={{ xs: 6, md: 6 }}>
                  <ColorDot color={value.isOpsSLABreached ? COLORS.Red : COLORS.Green} />
                  <Typography textColor={value.isOpsSLABreached ? COLORS.Red : COLORS.Green}>{value.opsSLA}</Typography>
                </GridAlignContainer>
              </MobileItemGrid>
          }
          <MobileItemGrid container>
            <Grid size={{ xs: 6, md: 6 }}>
              <TitleLabel>Scheduled</TitleLabel>
            </Grid>
            <Grid size={{ xs: 6, md: 6 }} display="flex" alignItems="center">
              <ColorDot color="gray" />
              <EditDateTimePicker
                isEditable={permissions.isScheduled}
                value={value.scheduled || ''}
                disablePast={true}
                onChange={(val) => onChange({
                  ...value,
                  scheduled: val
                })} />
            </Grid>
          </MobileItemGrid>
          <MobileItemGrid container>
            <Grid size={{ xs: 6, md: 6 }}>
              <TitleLabel>Priority</TitleLabel>
            </Grid>
            <Grid size={{ xs: 6, md: 6 }}>
              <EditPrioritySelectMenu
                id="service-summary-priority-select"
                isEditable={permissions.isPriority}
                items= {options.priorityAndServicesOptions?.map((obj: any) => ({ 
                  value: obj.id.toString(),
                  label: obj.value.shortName,
                  color: obj.value.color,
                  description: obj.value.description,
                  disabled: !obj.value.isActive
                })) || []}
                value={value.priorityId?.toString() || ''}
                onChange={(val) => onChange({
                  ...value,
                  priorityId: val
                })}
                priorityLabel={value.priorityLabel}
                priorityColor={value.priorityColor}
              />
            </Grid>
          </MobileItemGrid>
          <MobileItemGrid container>
            <Grid size={{ xs: 6, md: 6 }}>
              <TitleLabel>Service Type</TitleLabel>
            </Grid>
            <GridAlignContainer size={{ xs: 6, md: 6 }}>
              <ColorDot color={COLORS.DarkCyan} />
              <EditDropdownField
                textColor={COLORS.DarkCyan}
                textFontSize=""
                isEditable={permissions.isServiceType}
                options={serviceTypesData(options.serviceOptions || [])}
                value={value.serviceType || '-1'}
                onChange={(val) => onChange({
                  ...value,
                  serviceType: val
                })}
                validate={true}
                optionalLabel="Select Service Type"
                optionalLabelEnabled={true}
                errorMessageEnabled={false}
              />
            </GridAlignContainer>
          </MobileItemGrid>
          <MobileItemGrid container>
            <Grid size={{ xs: 6, md: 6 }}>
              <TitleLabel>Action Type</TitleLabel>
            </Grid>
            <GridAlignContainer size={{ xs: 6, md: 6 }}>
              <ColorDot color={COLORS.LightCyan} />
              <EditDropdownField
                textColor={COLORS.LightCyan}
                textFontSize=""
                isEditable={permissions.isActionType}
                options={actionTypesData(options.actionTypeOptions || [])}
                value={value.actionType || ''}
                onChange={(val) => onChange({
                  ...value,
                  actionType: val
                })}
                validate={true}
                optionalLabel="Select Action Type"
                optionalLabelEnabled={true}
                errorMessageEnabled={false}
              />
            </GridAlignContainer>
          </MobileItemGrid>
          <MobileItemGrid container>
            <Grid size={{ xs: 6, md: 6 }}>
              <TitleLabel>Process Type</TitleLabel>
            </Grid>
            <Grid size={{ xs: 6, md: 6 }}>
              <EditDropdownField
                textColor={COLORS.Red}
                isEditable={permissions.isProcessType}
                options={processTypesData(options.processTypeOptions || [])}
                value={value.processType || ''}
                onChange={(val) => onChange({
                  ...value,
                  processType: val
                })}
                validate={true}
                optionalLabel="Select Process Type"
                optionalLabelEnabled={true}
                errorMessageEnabled={false}
              />
            </Grid>
          </MobileItemGrid>
          <MobileItemGrid container>
            <Grid size={{ xs: 6, md: 6 }}>
              <TitleLabel>Site Schema</TitleLabel>
            </Grid>
            <Grid size={{ xs: 6, md: 6 }}>
              {value.siteSchema || '-'}
            </Grid>
          </MobileItemGrid>
        </Grid>
      }
    </>
  );
};